//From Chrome 107 on desktop and greater versions and from Chrome 110 on Android and greater versions,
//it is not possible anymore to identify the operational system version using the user agent string 
//and specificifally on Android, it is not possible anymore to identify theAndroid device model using the user agent string.
//From now on we need to use User Agent Client Hints and fallback to user agent string
//in case User Agent Client Hints are not supported by the browser.
//https://developers.google.com/privacy-sandbox/blog/user-agent-reduction-android-model-and-version
//https://docs.uaparser.dev/guides/how-to-detect-macos-10157-using-javascript.html
//https://docs.uaparser.dev/guides/how-to-detect-windows-11-using-javascript.html
//https://docs.uaparser.dev/guides/how-to-identify-android-k-device.html

import { INVALID_DEVICE_TYPES, IPHONE_HEIGHT_568, IPHONE_HEIGHT_667, IPHONE_HEIGHT_736, IPHONE_HEIGHT_812, IPHONE_HEIGHT_844, IPHONE_HEIGHT_852, IPHONE_HEIGHT_874, IPHONE_HEIGHT_896, IPHONE_HEIGHT_912, IPHONE_HEIGHT_926, IPHONE_HEIGHT_932, IPHONE_HEIGHT_956, SAFARI_INNER_HEIGHT_1435, SAFARI_INNER_HEIGHT_449, SAFARI_INNER_HEIGHT_548, SAFARI_INNER_HEIGHT_549, SAFARI_INNER_HEIGHT_617, SAFARI_INNER_HEIGHT_635, SAFARI_INNER_HEIGHT_659, SAFARI_INNER_HEIGHT_663, SAFARI_INNER_HEIGHT_664, SAFARI_INNER_HEIGHT_670, SAFARI_INNER_HEIGHT_678, SAFARI_INNER_HEIGHT_695, SAFARI_INNER_HEIGHT_699, SAFARI_INNER_HEIGHT_714, SAFARI_INNER_HEIGHT_719, SAFARI_INNER_HEIGHT_739, SAFARI_INNER_HEIGHT_746, SAFARI_INNER_HEIGHT_750, SAFARI_INNER_HEIGHT_754, SAFARI_INNER_HEIGHT_760, SAFARI_INNER_HEIGHT_775, SAFARI_INNER_HEIGHT_781, SAFARI_INNER_HEIGHT_796, SAFARI_INNER_HEIGHT_TOLERANCE } from "../constants/device";
import Logger from "./logging";

class DeviceHelperClass {

	constructor() {
		this.android = false;
		this.browserNameString = '';
		this.browserString = '';
		this.chrome = false;
		this.chromiumBrowser = false;
		this.chromeWebView = false;
		this.desktop = false;
		this.deviceString = '';
		this.deviceTypeString = '';
		this.facebook = false;
		this.instagram = false;
		this.ios = false;
		this.mobile = false;
		this.osNameString = '';
		this.safari = false;
		this.safariViewController = false;
		this.tablet = false;
	}

	async initializeBrowserDeviceData() {

		const userAgentParser = new window.UAParser();

		const browser = userAgentParser.getBrowser();

		const appVersion = browser.version;

		this.browserNameString = browser.name;

		//Handle Brave browser.
		if (navigator.brave) {
			this.browserNameString = 'Brave';
		}

		this.facebook = this.browserNameString === 'Facebook';

		this.instagram = this.browserNameString === 'Instagram';

		this.chromeWebView = this.browserNameString === 'Chrome WebView';

		this.chromiumBrowser = !this.chromeWebView && !this.facebook && !this.instagram && userAgentParser.getEngine().is('Blink');
	
		this.chrome = this.browserNameString === 'Chrome' || this.browserNameString === 'Mobile Chrome';
		this.safari = this.browserNameString === 'Safari' || this.browserNameString === 'Mobile Safari';
	
		const device = await userAgentParser.getDevice().withFeatureCheck().withClientHints();

		const deviceModel = device.model;
	
		const deviceType = device.type;

		this.deviceTypeString = deviceType;

		const os = await userAgentParser.getOS().withFeatureCheck().withClientHints();

		this.osNameString = os.name;

		//Corner case. iPad always returns it's operational system as macOS.
		if (deviceModel === 'iPad') {
			this.osNameString = 'iOS';
		}

		const osNameStringLowercase =  this.osNameString ? this.osNameString.toLowerCase() : '';

		this.android = osNameStringLowercase.includes('android');
		this.ios = this.osNameString === 'iOS';

		this.tablet = deviceType === 'tablet' || deviceModel === 'iPad';
		this.mobile = this.tablet || deviceType === 'mobile' || this.android || this.ios;

		//The library ua-parser-js doesn't return a specific string on device.type when the device is desktop.
		//The developer is responsible to identify when the device is a desktop.
		//So let's consider that the device is a desktop if it isn't a mobile device (smartphone or tablet)
		//nor the other device types identified by the library ua-parser-js.
		//https://docs.uaparser.dev/info/device/type.html
		this.desktop = !deviceType || !(this.mobile || INVALID_DEVICE_TYPES.includes(deviceType));

		const browserVersion = (this.facebook || this.instagram) ? appVersion : browser.version;

		let osVersion = os.version;

		if (this.ios) {
			let isIos26Plus;

			if (browserVersion) {
				let iosMajorVersion;

				if (osVersion) {
					const osVersions = osVersion.split('.');
					if (osVersions && osVersions.length) {
						iosMajorVersion = osVersions[0];
					}
				}

				if (iosMajorVersion && this.safari) {
					const browserVersions = browserVersion.split('.');
					if (browserVersions && browserVersions.length) {
						const browserMajorVersion = browserVersions[0];
						if (browserMajorVersion) {
							//Corner case: From Safari 26 and above, Safari stopped showing the correct iOS version in the user agent string
							//and it now always shows a previous version of iOS that does not match the Safari version.
							//https://webkit.org/blog/17333/webkit-features-in-safari-26-0/
							//All Safari major versions on iOS match the iOS major version (e.g. Safari 26.X only ships on iOS 26.X),
							//so a Safari major version of at least 26 means iOS 26 or above, whether or not the user agent froze
							//the reported iOS version. Relying on the browser version alone also handles devices that report the
							//real iOS version (e.g. iOS 26), which the previous "iOS < 26" guard incorrectly routed to the legacy path.
							isIos26Plus = parseInt(browserMajorVersion) >= 26;

							//Get the correct iOS version when the Safari version is at least 26.
							//The iOS version matches the Safari version that comes installed in the system.
							if (isIos26Plus) {
								osVersion = browserVersion;
							}
							else {
								//Corner case: Some iOS apps like the Prodege apps are overriding the user agent and using a custom one that is
								//making our code identify the web view inside the app as Safari, but with a different version than the Safari version that comes
								//with iOS. All Safari major versions on iOS, match the iOS major version. For example, Safari 18.X is only available on iOS 18.X.
								//So if the Safari major version doesn't match the iOS major version, this means that the user is not really accessing the Safari that comes with iOS.
								if (browserMajorVersion !== iosMajorVersion) {
									this.safari = false;
								}
							}
						}
					}
				}
			}

			//Corner case: block study flow when the study link is accessed in SFSafariViewController used by some iOS apps like GMail and Slack.
			//When the study link is accessed in SFSafariViewController, the video is not playing. But DRM is working.
			//As DRM is working on SFSafariViewController, in the future we should fix the video not playing and allow users to take the study on SFSafariViewController.
			this.safariViewController = this.validateSafariViewController(isIos26Plus);
		}

		const browserData = [];

		if (this.safariViewController) {
			browserData.push('SFSafariViewController');
		} else if (this.safari) {
			browserData.push('Safari');
		} else if (this.chrome) {
			browserData.push('Chrome');
		} else {
			browserData.push(this.browserNameString);
		}

		if (browserVersion) {
			browserData.push(browserVersion);
		}

		this.browserString = browserData.join(' ');

		const deviceData = [];

		if (deviceModel) deviceData.push(deviceModel);

		if (this.osNameString) deviceData.push(this.osNameString);

		
		if (osVersion) deviceData.push(osVersion);

		this.deviceString = deviceData.join(' ');

		Logger.log(this.constructor.name, 'initializeBrowserDeviceData()', 'device type', deviceType, 'browser', this.browserString, 'device', this.deviceString);
	}

	//There is currently no better way to distinguish between SFSafariViewController and Safari on iOS.
	//They are identical. Same features, same attributes exposed by Web APIs and same user agent.
	//But debugging I noticed that the inner height of SFSafariViewController is greater than the inner height of Safari.
	//As SFSafariViewController renders the content from top to bottom of the physical screen and part of the content is rendered behind the bottom and top navigation bars.
	//So we can identify if the page is inside SFSafariViewController when it's inner height is greater than the inner height in Safari.
	//This is true just for the iOS version before iOS 26.
	//From iOS 26 and above, Safari has a floating address bar at the bottom of the screen and there is no top navigation bar.
	//So the content is rendered from top to bottom of the screen. And it is also rendered behind the bottom floating address bar.
	//So we can identify if the page is inside SFSafariViewController in iOS 26 or above, when it's inner height is less than the inner height in Safari.
	//TODO: Find a better way to distinguish between SFSafariViewController and Safari on iOS.
	validateSafariViewController(isIos26Plus) {
		const deviceSafariConfigs = {
			//iPhone 5, iPhone 5C, iPhone 5S, iPhone SE 1st gen, iPod touch 5th gen, iPod touch 6th gen, iPod touch 7th gen
			[IPHONE_HEIGHT_568]: {
				legacyInnerHeight: SAFARI_INNER_HEIGHT_449
			},

			//iPhone 6, iPhone 6s, iPhone 7, iPhone 8, iPhone SE 2nd gen, iPhone SE 3rd gen
			[IPHONE_HEIGHT_667]: {
				legacyInnerHeight: SAFARI_INNER_HEIGHT_548,
				ios26InnerHeight: [SAFARI_INNER_HEIGHT_549, SAFARI_INNER_HEIGHT_1435]
			},

			//iPhone 6 Plus, iPhone 6s Plus, iPhone 7 Plus, iPhone 8 Plus
			[IPHONE_HEIGHT_736]: {
				legacyInnerHeight: SAFARI_INNER_HEIGHT_617
			},

			//iPhone X, iPhone XS, iPhone 11 Pro, iPhone 12 mini, iPhone 13 mini
			[IPHONE_HEIGHT_812]: {
				legacyInnerHeight: SAFARI_INNER_HEIGHT_635,
				ios26InnerHeight: [SAFARI_INNER_HEIGHT_664, SAFARI_INNER_HEIGHT_670]
			},

			//iPhone 12, iPhone 12 Pro, iPhone 13, iPhone 13 Pro, iPhone 14, iPhone 16e
			[IPHONE_HEIGHT_844]: {
				// Special case: depends on innerHeight
				special: true,
				ios26InnerHeight: SAFARI_INNER_HEIGHT_699,
				legacyCheck: {
					threshold: SAFARI_INNER_HEIGHT_664,
					fallback: SAFARI_INNER_HEIGHT_663
				}
			},

			//iPhone 14 Pro, iPhone 15, iPhone 15 Pro, iPhone 16
			[IPHONE_HEIGHT_852]: {
				legacyInnerHeight: SAFARI_INNER_HEIGHT_659,
				ios26InnerHeight: SAFARI_INNER_HEIGHT_695
			},

			//iPhone 16 Pro
			[IPHONE_HEIGHT_874]: {
				legacyInnerHeight: SAFARI_INNER_HEIGHT_678,
				ios26InnerHeight: SAFARI_INNER_HEIGHT_714
			},

			//iPhone XR, iPhone XS Max, iPhone 11, iPhone 11 Pro Max
			[IPHONE_HEIGHT_896]: {
				legacyInnerHeight: SAFARI_INNER_HEIGHT_719,
				ios26InnerHeight: [SAFARI_INNER_HEIGHT_750, SAFARI_INNER_HEIGHT_754]
			},

			//iPhone Air
			[IPHONE_HEIGHT_912]: {
				ios26InnerHeight: SAFARI_INNER_HEIGHT_781
			},

			//iPhone 12 Pro Max, iPhone 13 Pro Max, iPhone 14 Plus
			[IPHONE_HEIGHT_926]: {
				legacyInnerHeight: SAFARI_INNER_HEIGHT_746,
				ios26InnerHeight: SAFARI_INNER_HEIGHT_781
			},

			//iPhone 14 Pro Max, iPhone 15 Plus, iPhone 15 Pro Max, iPhone 16 Plus
			[IPHONE_HEIGHT_932]: {
				legacyInnerHeight: SAFARI_INNER_HEIGHT_739,
				ios26InnerHeight: SAFARI_INNER_HEIGHT_775
			},

			//iPhone 16 Pro Max
			[IPHONE_HEIGHT_956]: {
				legacyInnerHeight: SAFARI_INNER_HEIGHT_760,
				ios26InnerHeight: SAFARI_INNER_HEIGHT_796
			}
		};

		const config = deviceSafariConfigs[window.outerHeight];
		if (!config) return false;

		// Special case for height 844
		if (config.special) {
			if (isIos26Plus) {
				return window.innerHeight < config.ios26InnerHeight - SAFARI_INNER_HEIGHT_TOLERANCE;
			}

			if (window.innerHeight > config.legacyCheck.threshold + SAFARI_INNER_HEIGHT_TOLERANCE) {
				return true;
			}

			return window.innerHeight > config.legacyCheck.fallback + SAFARI_INNER_HEIGHT_TOLERANCE;
		}

		// Select the correct inner height(s)
		const safariInnerHeight = isIos26Plus ? config.ios26InnerHeight : config.legacyInnerHeight;
		if (!safariInnerHeight) return false;

		// For iOS 26+, some devices share an outer height across sub-models that each have a
		// slightly different Safari inner height. Real Safari matches one of those heights, while
		// SFSafariViewController renders shorter than all of them. So only flag it as
		// SFSafariViewController when the inner height is below the smallest valid Safari height
		// (minus tolerance) - comparing against the largest would wrongly flag real Safari on the
		// shorter sub-models.
		if (Array.isArray(safariInnerHeight)) {
			return window.innerHeight < Math.min(...safariInnerHeight) - SAFARI_INNER_HEIGHT_TOLERANCE;
		}

		// Single target height
		return isIos26Plus
			? window.innerHeight < safariInnerHeight - SAFARI_INNER_HEIGHT_TOLERANCE
			: window.innerHeight > safariInnerHeight + SAFARI_INNER_HEIGHT_TOLERANCE;
	}

	get browser() {
		return this.browserString;
	}

	get browserName() {
		return this.browserNameString;
	}

	get device() {
		return this.deviceString;
	}

	get deviceType() {
		return this.deviceTypeString;
	}

	get isAndroid() {
		return this.android;
	}

	get isChrome() {
		return this.chrome;
	}

	get isChromiumBrowser() {
		return this.chromiumBrowser;
	}

	get isChromeWebView() {
		return this.chromeWebView;
	}

	get isDesktop() {
		return this.desktop;
	}

	get isFacebook() {
		return this.facebook;
	}

	get isInstagram() {
		return this.instagram;
	}

	get isIOS() {
		return this.ios;
	}

	get isMobile() {
		return this.mobile;
	}

	get isSafari() {
		return this.safari;
	}

	get isSafariViewController() {
		return this.safariViewController;
	}

	get isTablet() {
		return this.tablet;
	}

	get osName() {
		return this.osNameString;
	}
}

const DeviceHelper = new DeviceHelperClass();

export default DeviceHelper;


// WEBPACK FOOTER //
// ./src/functions/device.js